import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingCart, Search, Package, Store, LayoutDashboard, LogOut, User, Menu, X, ClipboardList, Crown, UserCog, Warehouse, Building2, Home, PackageSearch } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const submit = (e) => { e.preventDefault(); nav(`/katalog?q=${encodeURIComponent(q)}`); setOpen(false); };

  const role = user?.role;
  const isWarehouse = role === "gudang_china" || role === "gudang_jakarta";
  const isStaff = ["owner", "admin", "induk_kedai", "gudang_china", "gudang_jakarta", "agent"].includes(role);
  const brandTo = isWarehouse ? "/gudang" : role === "induk_kedai" ? "/induk" : role === "agent" ? "/kedai-panel" : (role === "owner" || role === "admin") ? "/admin" : "/";

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/85 border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-4 h-16">
          <Link to={brandTo} data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
            {isWarehouse ? (
              <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center text-white font-display font-extrabold">G</div>
            ) : (
              <img src="/kedai-logo.png" alt="Kedai Impor" className="w-9 h-9 object-contain" />
            )}
            <span className="font-display font-extrabold text-lg text-stone-900 leading-none">{isWarehouse ? "Portal Gudang" : <>Kedai <span className="text-emerald-600">Impor</span></>}</span>
          </Link>

          {!isStaff && (
            <form onSubmit={submit} className="flex-1 max-w-xl hidden md:flex items-center bg-stone-100 rounded-full px-4 py-2 border border-transparent focus-within:border-emerald-400 transition">
              <Search size={18} className="text-stone-400" />
              <input data-testid="nav-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Cari barang import pakai Bahasa Indonesia..."
                className="bg-transparent outline-none px-3 flex-1 text-sm" />
              <button data-testid="nav-search-btn" type="submit" className="text-xs font-semibold text-emerald-700">Cari</button>
            </form>
          )}

          <div className={`flex items-center gap-1 sm:gap-3 ${isStaff ? "ml-auto" : "ml-auto"}`}>
            {!isStaff && (
              <>
                <Link to="/" data-testid="nav-home" className="hidden sm:flex items-center gap-1 text-sm font-medium text-stone-600 hover:text-emerald-700 px-2"><Home size={17} />Beranda</Link>
                <Link to="/katalog" data-testid="nav-katalog" className="hidden sm:flex items-center gap-1 text-sm font-medium text-stone-600 hover:text-emerald-700 px-2"><Package size={17} />Katalog</Link>
                <Link to="/kedai" data-testid="nav-kedai" className="hidden sm:flex items-center gap-1 text-sm font-medium text-stone-600 hover:text-emerald-700 px-2"><Store size={17} />Kedai</Link>
                <Link to="/lacak" data-testid="nav-track" className="hidden sm:flex items-center gap-1 text-sm font-medium text-stone-600 hover:text-emerald-700 px-2"><PackageSearch size={17} />Lacak</Link>
              </>
            )}
            {role === "user" && (
              <>
                <Link to="/permintaan" data-testid="nav-requests" className="hidden sm:flex items-center gap-1 text-sm font-medium text-stone-600 hover:text-emerald-700 px-2"><ClipboardList size={17} />Permintaan</Link>
                <Link to="/member" data-testid="nav-member" className="hidden sm:flex items-center gap-1 text-sm font-medium text-amber-600 hover:text-amber-700 px-2"><Crown size={16} />{user.is_member ? "Member" : "Jadi Member"}</Link>
                <Link to="/pesanan" data-testid="nav-orders" className="hidden sm:flex items-center gap-1 text-sm font-medium text-stone-600 hover:text-emerald-700 px-2"><Package size={17} />Pesanan</Link>
              </>
            )}
            {(role === "owner" || role === "admin") && (
              <Link to="/admin" data-testid="nav-admin" className="hidden sm:flex items-center gap-1 text-sm font-medium text-secondary hover:text-amber-700 px-2"><LayoutDashboard size={17} />{role === "owner" ? "Dashboard" : "Admin"}</Link>
            )}
            {role === "owner" && (
              <Link to="/kelola-akun" data-testid="nav-kelola" className="hidden sm:flex items-center gap-1 text-sm font-medium text-stone-600 hover:text-emerald-700 px-2"><UserCog size={17} />Kelola Akun</Link>
            )}
            {isWarehouse && (
              <Link to="/gudang" data-testid="nav-gudang" className="hidden sm:flex items-center gap-1 text-sm font-medium text-sky-700 hover:text-sky-800 px-2"><Warehouse size={17} />Panel Gudang</Link>
            )}
            {role === "induk_kedai" && (
              <Link to="/induk" data-testid="nav-induk" className="hidden sm:flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-800 px-2"><Building2 size={17} />Induk Kedai</Link>
            )}
            {role === "agent" && (
              <Link to="/kedai-panel" data-testid="nav-agent" className="hidden sm:flex items-center gap-1 text-sm font-medium text-secondary hover:text-amber-700 px-2"><Store size={17} />Panel Kedai</Link>
            )}

            {!isStaff && (
              <Link to="/keranjang" data-testid="nav-cart" className="relative p-2 text-stone-700 hover:text-emerald-700">
                <ShoppingCart size={22} />
                {count > 0 && <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-secondary text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{count}</span>}
              </Link>
            )}

            {user ? (
              <div className="flex items-center gap-2">
                <span className="hidden sm:flex items-center gap-1 text-sm text-stone-700"><User size={16} />{user.name?.split(" ")[0]}</span>
                <button data-testid="nav-logout" onClick={() => { logout(); nav("/"); }} className="p-2 text-stone-500 hover:text-red-600"><LogOut size={18} /></button>
              </div>
            ) : (
              <Link to="/login" data-testid="nav-login" className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-full transition">Masuk</Link>
            )}
            <button onClick={() => setOpen(!open)} className="md:hidden p-2">{open ? <X /> : <Menu />}</button>
          </div>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-stone-200 px-4 py-3 space-y-3 bg-white">
          {!isStaff && (
            <form onSubmit={submit} className="flex items-center bg-stone-100 rounded-full px-4 py-2">
              <Search size={18} className="text-stone-400" />
              <input data-testid="nav-search-input-mobile" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari barang..." className="bg-transparent outline-none px-3 flex-1 text-sm" />
            </form>
          )}
          <div className="flex flex-col gap-2 text-sm font-medium text-stone-700">
            {!isStaff && <Link to="/" data-testid="nav-home-mobile" onClick={() => setOpen(false)}>Beranda</Link>}
            {!isStaff && <Link to="/katalog" onClick={() => setOpen(false)}>Katalog</Link>}
            {!isStaff && <Link to="/kedai" onClick={() => setOpen(false)}>Kedai Lokal</Link>}
            {!isStaff && <Link to="/lacak" data-testid="nav-track-mobile" onClick={() => setOpen(false)}>Lacak Pesanan</Link>}
            {role === "user" && <Link to="/member" onClick={() => setOpen(false)}>{user.is_member ? "Status Member" : "Jadi Member"}</Link>}
            {role === "user" && <Link to="/permintaan" onClick={() => setOpen(false)}>Permintaan Cek Harga</Link>}
            {role === "user" && <Link to="/pesanan" onClick={() => setOpen(false)}>Pesanan Saya</Link>}
            {(role === "owner" || role === "admin") && <Link to="/admin" onClick={() => setOpen(false)}>{role === "owner" ? "Dashboard Owner" : "Admin"}</Link>}
            {role === "owner" && <Link to="/kelola-akun" onClick={() => setOpen(false)}>Kelola Akun</Link>}
            {isWarehouse && <Link to="/gudang" onClick={() => setOpen(false)}>Panel Gudang</Link>}
            {role === "induk_kedai" && <Link to="/induk" onClick={() => setOpen(false)}>Induk Kedai</Link>}
            {role === "agent" && <Link to="/kedai-panel" onClick={() => setOpen(false)}>Panel Kedai</Link>}
          </div>
        </div>
      )}
    </header>
  );
};
